import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiAlertTriangle,
  FiChevronRight,
  FiPlus,
  FiStar,
  FiTag,
  FiTrash2,
} from "react-icons/fi";
import axiosInstance from "../config/axiosInstance";
import NoData from "../components/NoData";
import ResponsiveModal from "../components/ResponsiveModal";
import { extractList } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import "./CategoriesPage.css";

const getCategoryName = (category) => category?.name || category?.nome || "-";

const isCategoryActive = (category) =>
  typeof category?.isActive === "boolean"
    ? category.isActive
    : String(category?.status || "").toLowerCase() === "ativo";

const isCategoryFeatured = (category) =>
  typeof category?.isFeatured === "boolean"
    ? category.isFeatured
    : String(category?.featured || category?.destaque || "").toLowerCase() === "true";

const CategoriesPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [name, setName] = useState("");
  const [featured, setFeatured] = useState(false);
  const [error, setError] = useState("");
  const [togglingId, setTogglingId] = useState(null);
  const [featuredTogglingId, setFeaturedTogglingId] = useState(null);
  const [selectedDeleteCategory, setSelectedDeleteCategory] = useState(null);
  const [deletePreview, setDeletePreview] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [replacementCategoryId, setReplacementCategoryId] = useState("");

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/categories", {
        params: { itemsPerPage: -1 },
      });
      setCategories(extractList(response));
    } catch {
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) fetchCategories();
  }, [isAdmin]);

  const handleToggle = async (category, currentlyActive) => {
    setTogglingId(category.id);
    try {
      await axiosInstance.put(`/categories/${category.id}/status`, {
        isActive: !currentlyActive,
      });
      setCategories((prev) =>
        prev.map((c) =>
          c.id === category.id ? { ...c, isActive: !currentlyActive } : c
        )
      );
    } catch {
    } finally {
      setTogglingId(null);
    }
  };

  const handleToggleFeatured = async (category, currentlyFeatured) => {
    setFeaturedTogglingId(category.id);
    try {
      await axiosInstance.put(`/categories/${category.id}/featured`, {
        isFeatured: !currentlyFeatured,
      });
      setCategories((prev) =>
        prev.map((c) =>
          c.id === category.id ? { ...c, isFeatured: !currentlyFeatured } : c
        )
      );
    } catch {
    } finally {
      setFeaturedTogglingId(null);
    }
  };

  const parentCategories = useMemo(() => {
    const parents = categories.filter((c) => !c.parentId);
    return parents.map((parent) => {
      const derived = categories.filter((c) => c.parentId === parent.id);
      const subs =
        Array.isArray(parent.subcategories) && parent.subcategories.length > 0
          ? parent.subcategories
          : derived;
      return { ...parent, subcategories: subs };
    });
  }, [categories]);

  const filteredCategories = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return parentCategories;
    return parentCategories.filter((c) =>
      getCategoryName(c).toLowerCase().includes(q)
    );
  }, [parentCategories, searchTerm]);

  const activeCount = parentCategories.filter(isCategoryActive).length;
  const featuredCount = parentCategories.filter(isCategoryFeatured).length;

  const totalSubs = parentCategories.reduce(
    (acc, c) => acc + (c.subcategories?.length ?? 0),
    0
  );

  const replacementOptions = useMemo(() => {
    const removedIds = new Set(
      deletePreview?.categoryIds ||
        (selectedDeleteCategory ? [selectedDeleteCategory.id] : [])
    );
    const categoryById = new Map(categories.map((category) => [category.id, category]));

    return categories
      .filter((category) => !removedIds.has(category.id))
      .map((category) => {
        const parent = category.parentId ? categoryById.get(category.parentId) : null;
        const label = parent
          ? `${getCategoryName(parent)} / ${getCategoryName(category)}`
          : getCategoryName(category);

        return { id: category.id, label };
      })
      .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
  }, [categories, deletePreview, selectedDeleteCategory]);

  const handleCreateCategory = async (event) => {
    event.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Informe o nome da categoria.");
      return;
    }
    setError("");
    setCreating(true);
    try {
      await axiosInstance.post("/categories", {
        name: trimmedName,
        isFeatured: featured,
      });
      setName("");
      setFeatured(false);
      setIsCreateModalOpen(false);
      fetchCategories();
    } catch (createError) {
      setError(
        createError?.response?.data?.message ||
          "Não foi possível cadastrar a categoria."
      );
    } finally {
      setCreating(false);
    }
  };

  const closeDeleteModal = () => {
    if (deleteSubmitting) return;
    setSelectedDeleteCategory(null);
    setDeletePreview(null);
    setDeleteError("");
    setReplacementCategoryId("");
  };

  const handleOpenDeleteModal = async (category) => {
    setSelectedDeleteCategory(category);
    setDeletePreview(null);
    setDeleteError("");
    setReplacementCategoryId("");
    setDeleteLoading(true);

    try {
      const response = await axiosInstance.get(
        `/categories/${category.id}/delete-preview`
      );
      setDeletePreview(response?.data?.data || null);
    } catch (previewError) {
      setDeleteError(
        previewError?.response?.data?.message ||
          "Não foi possível verificar os vínculos dessa categoria."
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!selectedDeleteCategory) return;

    const productsCount = deletePreview?.productsCount || 0;
    if (productsCount > 0 && !replacementCategoryId) {
      setDeleteError("Escolha uma categoria para receber os produtos vinculados.");
      return;
    }

    setDeleteSubmitting(true);
    setDeleteError("");

    try {
      const response = await axiosInstance.delete(
        `/categories/${selectedDeleteCategory.id}`,
        {
          data: replacementCategoryId ? { replacementCategoryId } : {},
        }
      );
      const deletedIds =
        response?.data?.data?.categoryIds ||
        deletePreview?.categoryIds ||
        [selectedDeleteCategory.id];

      setCategories((prev) =>
        prev.filter((category) => !deletedIds.includes(category.id))
      );
      closeDeleteModal();
      fetchCategories();
    } catch (deleteRequestError) {
      setDeleteError(
        deleteRequestError?.response?.data?.message ||
          "Não foi possível excluir a categoria."
      );
    } finally {
      setDeleteSubmitting(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="fade-in page-stack">
        <NoData
          title="Acesso restrito"
          description="A página de categorias é exclusiva para administradores."
        />
      </div>
    );
  }

  return (
    <div className="fade-in page-stack">
      <ResponsiveModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          if (creating) return;
          setIsCreateModalOpen(false);
        }}
        title="Nova categoria"
        subtitle="cadastro"
        size="sm"
      >
        <form onSubmit={handleCreateCategory} className="space-y-3">
          <div className="field" style={{ margin: 0 }}>
            <label className="label">Nome da categoria</label>
            <input
              className="input"
              type="text"
              placeholder="Ex.: Grãos, Hortifruti, Laticínios"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              required
            />
          </div>
          <label className="cat-check">
            <input
              type="checkbox"
              checked={featured}
              onChange={(e) => setFeatured(e.target.checked)}
            />
            <span>Categoria em destaque</span>
          </label>
          {error ? <div className="alert">{error}</div> : null}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
            <button
              className="button button--ghost"
              type="button"
              onClick={() => { if (creating) return; setIsCreateModalOpen(false); }}
              disabled={creating}
            >
              Cancelar
            </button>
            <button className="button button--primary" type="submit" disabled={creating}>
              {creating ? "Cadastrando..." : "Cadastrar"}
            </button>
          </div>
        </form>
      </ResponsiveModal>

      <ResponsiveModal
        isOpen={Boolean(selectedDeleteCategory)}
        onClose={closeDeleteModal}
        title="Excluir categoria"
        subtitle="confirmação"
        size="sm"
      >
        <div className="cat-delete-modal">
          <div className="cat-delete-warning">
            <FiAlertTriangle size={20} />
            <div>
              <strong>{getCategoryName(selectedDeleteCategory)}</strong>
              <p>
                Essa ação não pode ser desfeita. Confirme antes de remover a
                categoria.
              </p>
            </div>
          </div>

          {deleteLoading ? (
            <div className="muted">Verificando produtos vinculados...</div>
          ) : deletePreview ? (
            <>
              {deletePreview?.subcategoriesCount > 0 ? (
                <div className="cat-delete-note">
                  {deletePreview.subcategoriesCount} subcategor
                  {deletePreview.subcategoriesCount === 1 ? "ia será excluída" : "ias serão excluídas"} junto.
                </div>
              ) : null}

              {(deletePreview?.productsCount || 0) > 0 ? (
                <div className="field" style={{ margin: 0 }}>
                  <label className="label">
                    Enviar {deletePreview.productsCount} produto
                    {deletePreview.productsCount === 1 ? "" : "s"} vinculado
                    {deletePreview.productsCount === 1 ? "" : "s"} para
                  </label>
                  <select
                    className="input"
                    value={replacementCategoryId}
                    onChange={(event) => setReplacementCategoryId(event.target.value)}
                    disabled={replacementOptions.length === 0}
                  >
                    <option value="">Selecione a categoria de destino</option>
                    {replacementOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {replacementOptions.length === 0 ? (
                    <p className="cat-delete-help">
                      Crie outra categoria antes de excluir esta.
                    </p>
                  ) : null}
                </div>
              ) : (
                <div className="cat-delete-note">
                  Nenhum produto vinculado será movido.
                </div>
              )}
            </>
          ) : null}

          {deleteError ? <div className="alert">{deleteError}</div> : null}

          <div className="cat-delete-actions">
            <button
              className="button button--ghost"
              type="button"
              onClick={closeDeleteModal}
              disabled={deleteSubmitting}
            >
              Cancelar
            </button>
            <button
              className="button cat-delete-confirm"
              type="button"
              onClick={handleDeleteCategory}
              disabled={
                deleteLoading ||
                deleteSubmitting ||
                !deletePreview ||
                ((deletePreview?.productsCount || 0) > 0 && !replacementCategoryId)
              }
            >
              {deleteSubmitting ? "Excluindo..." : "Excluir"}
            </button>
          </div>
        </div>
      </ResponsiveModal>

      <div className="page-header">
        <div>
          <h2>Categorias</h2>
          <p>Organize os produtos por categoria e subcategoria.</p>
        </div>
        <button
          className="button button--primary"
          type="button"
          onClick={() => setIsCreateModalOpen(true)}
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <FiPlus size={15} />
          Nova categoria
        </button>
      </div>

      <div className="cat-stats">
        <div className="cat-stat">
          <div className="cat-stat__label">Categorias</div>
          <div className="cat-stat__value">{parentCategories.length}</div>
        </div>
        <div className="cat-stat">
          <div className="cat-stat__label">Ativas</div>
          <div className="cat-stat__value cat-stat__value--accent">{activeCount}</div>
        </div>
        <div className="cat-stat">
          <div className="cat-stat__label">Subcategorias</div>
          <div className="cat-stat__value">{totalSubs}</div>
        </div>
        <div className="cat-stat">
          <div className="cat-stat__label">Destaques</div>
          <div className="cat-stat__value cat-stat__value--featured">{featuredCount}</div>
        </div>
      </div>

      <div className="section-card">
        <div className="cat-toolbar">
          <input
            className="input"
            type="text"
            placeholder="Buscar categoria..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {!loading && filteredCategories.length > 0 && (
            <span className="cat-count">
              {filteredCategories.length}{" "}
              {filteredCategories.length === 1 ? "resultado" : "resultados"}
            </span>
          )}
        </div>

        {loading ? (
          <div className="muted" style={{ paddingTop: 24, textAlign: "center" }}>
            Carregando...
          </div>
        ) : parentCategories.length === 0 ? (
          <NoData
            title="Nenhuma categoria cadastrada"
            description="Crie a primeira categoria para organizar os produtos."
          />
        ) : filteredCategories.length === 0 ? (
          <NoData title="Nenhum resultado" description="Tente ajustar o termo buscado." />
        ) : (
          <div className="cat-list">
            {filteredCategories.map((category) => {
              const isActive = isCategoryActive(category);
              const isFeatured = isCategoryFeatured(category);
              const subCount = category.subcategories?.length ?? 0;
              const isToggling = togglingId === category.id;
              const isFeaturedToggling = featuredTogglingId === category.id;

              return (
                <div key={category.id} className="cat-row">
                  <FiTag size={15} className={`cat-row__icon${!isActive ? " cat-row__icon--inactive" : ""}`} />

                  <Link
                    to={`/admin/categorias/${category.id}/subcategorias`}
                    className="cat-row__link"
                  >
                    <span className="cat-row__name">
                      {getCategoryName(category)}
                    </span>
                    <span className="pill pill--sub" style={{ marginLeft: 8 }}>
                      {subCount} subcategor{subCount === 1 ? "ia" : "ias"}
                    </span>
                    <FiChevronRight size={15} className="cat-row__arrow" style={{ marginLeft: "auto" }} />
                  </Link>

                  <div className="cat-row__actions">
                    <button
                      className={`cat-featured ${
                        isFeatured ? "cat-featured--on" : "cat-featured--off"
                      }`}
                      type="button"
                      onClick={() => handleToggleFeatured(category, isFeatured)}
                      disabled={isFeaturedToggling}
                      title={isFeatured ? "Remover dos destaques" : "Marcar como destaque"}
                    >
                      <FiStar size={13} />
                      {isFeaturedToggling ? "..." : isFeatured ? "Destaque" : "Normal"}
                    </button>

                    <button
                      className={`cat-toggle ${isActive ? "cat-toggle--on" : "cat-toggle--off"}`}
                      type="button"
                      onClick={() => handleToggle(category, isActive)}
                      disabled={isToggling}
                      title={isActive ? "Clique para desativar" : "Clique para ativar"}
                    >
                      {isToggling ? "..." : isActive ? "Ativa" : "Inativa"}
                    </button>

                    <button
                      className="cat-delete"
                      type="button"
                      onClick={() => handleOpenDeleteModal(category)}
                      title="Excluir categoria"
                    >
                      <FiTrash2 size={13} />
                      Excluir
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoriesPage;
